import { PortalMode, portalStoreToken, usePortalStore } from '@servicetitan/cp-ui';
import {
    BodyText,
    Button,
    Icon,
    Link as AnvilLink,
    Stack,
    SideNav,
    IconNameType,
    Eyebrow,
    Link,
} from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { tokens } from '@servicetitan/tokens/core';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, Fragment, ReactNode } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { termsLink } from '../../../auth/utils/terms';
import { NavigationStore } from '../../stores/navigation.store';
import { IWebPortalStore, WebPortalStore } from '../../stores/portal.store';
import { SchedulingWrapper } from '../scheduler/scheduling-wrapper';
import * as Styles from './portal-layout-menu.module.less';

interface MenuLinkProps {
    className?: string;
    pathname: string;
    exact?: boolean;
    children?: ReactNode;
    icon?: IconNameType;
    onClick(): void;
}

const MenuLink: FC<MenuLinkProps> = props => {
    const { children, exact, icon, onClick, pathname } = props;

    const navigate = useNavigate();
    const store = usePortalStore() as IWebPortalStore;
    const location = useLocation();
    const match = useMatch(pathname + '/*');

    const active = exact ? location.pathname === pathname : !!match;

    const handleClick = () => {
        store.cpMfeResponse('redirect', pathname, Date.now());
        onClick();
        navigate(pathname);
    };

    return (
        <SideNav.Item
            data-cy={`portal-layout-menu-link-${pathname}`}
            to={pathname}
            active={active}
            icon={icon}
            onClick={handleClick}
            className="m-b-1"
        >
            {children}
        </SideNav.Item>
    );
};

interface PortalLayoutMenuProps {
    authorized: boolean;
    menuOpen: boolean;
    mode: PortalMode;
    onClose: () => void;
    onSignOut: () => void;
}

export const PortalLayoutMenu: FC<PortalLayoutMenuProps> = observer(props => {
    const { authorized, menuOpen, mode, onClose, onSignOut } = props;
    const [{ navigationLinks }] = useDependencies(NavigationStore);
    const [{ settings }] = useDependencies<[WebPortalStore]>(portalStoreToken as any);

    const closeMenu = () => {
        onClose();
    };

    return (
        <div
            className={classNames(
                mode.sidebarMenu ? Styles.layoutSidebar : Styles.layoutBurger,
                {
                    [Styles.menuOpened]: !mode.sidebarMenu && menuOpen,
                },
                Styles.menuWrapper
            )}
            data-tracking-section="portal-layout-sidenav"
            data-cy="portal-layout-sidenav"
        >
            <div className={Styles.menu}>
                {!mode.sidebarMenu && (
                    <Fragment>
                        <Stack justifyContent="flex-end" className="p-1 p-b-0">
                            <Button
                                iconName="close"
                                fill="subtle"
                                onClick={closeMenu}
                                aria-label="Menu"
                            />
                        </Stack>

                        {authorized && mode.mobile && settings?.bookingEnabled && (
                            <div className="p-x-2 m-y-2">
                                <SchedulingWrapper />
                            </div>
                        )}
                    </Fragment>
                )}

                <SideNav
                    className={classNames(Styles.menuLinks, {
                        'p-t-4': mode.sidebarMenu,
                        'p-t-0': !mode.sidebarMenu,
                    })}
                >
                    {navigationLinks.map(link => (
                        <MenuLink
                            key={link.path}
                            pathname={link.path}
                            exact={link.exact}
                            onClick={closeMenu}
                            icon={link.icon}
                        >
                            {link.title}
                        </MenuLink>
                    ))}
                </SideNav>

                <div className="p-x-2 m-b-2">
                    {(!!settings?.phoneNumberFormatted || !!settings?.email) && (
                        <Eyebrow size="medium" className="m-b-2">
                            Contact us
                        </Eyebrow>
                    )}

                    {!!settings?.phoneNumberFormatted && (
                        <Stack alignItems="center" className="m-t-1">
                            <Icon name="phone" className="m-r-1" color={tokens.colorNeutral200} />
                            <AnvilLink href={`tel:${settings.phoneNumber}`}>
                                <BodyText size="small" className={Styles.contactPhone}>
                                    {settings.phoneNumberFormatted}
                                </BodyText>
                            </AnvilLink>
                        </Stack>
                    )}
                    {!!settings?.email && (
                        <Stack alignItems="center" className="m-t-1">
                            <Icon name="email" className="m-r-1" color={tokens.colorNeutral200} />
                            <AnvilLink href={`mailto:${settings.email}`}>
                                <BodyText size="small" className={Styles.contactEmail}>
                                    {settings.email}
                                </BodyText>
                            </AnvilLink>
                        </Stack>
                    )}
                </div>

                {!mode.sidebarMenu && (
                    <div className="border-top p-x-2 p-y-1">
                        <Button fill="subtle" onClick={onSignOut} data-tracking-id="signout-button">
                            Sign out
                        </Button>
                    </div>
                )}

                <div className="p-y-2 p-l-4 p-r-2 border-top">
                    <Eyebrow size="small" className="m-b-half">
                        powered by
                    </Eyebrow>
                    <img
                        src="https://static.servicetitan.com/portal/st-logo.svg"
                        alt="ServiceTitan"
                        className={Styles.menuStLogo}
                    />
                </div>

                {!mode.sidebarMenu && (
                    <Stack className="border-top p-2" alignItems="center" justifyContent="center">
                        <Link href={termsLink} target="_blank" className="lh-default">
                            Terms of Service
                        </Link>
                    </Stack>
                )}
            </div>
            <div className={Styles.menuShadow} onClick={closeMenu} />
        </div>
    );
});
